/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  pages: {
    all: ["pages"] as const,
    /** Complete tree of visible pages (own + shared). */
    tree: (userId: string | undefined) => ["pages", "tree", userId] as const,
    /** Page detail. */
    detail: (pageId: string | undefined) => ["pages", "detail", pageId] as const,
    /** Page blocks in document order. */
    blocks: (pageId: string | undefined) => ["pages", "blocks", pageId] as const,
    /** All user blocks (analytics: word count, link map). */
    blocksAll: (userId: string | undefined) => ["pages", "blocksAll", userId] as const,
    /** Page search by title. */
    search: (term: string) => ["pages", "search", term] as const,
    /** Page attachments. */
    attachments: (pageId: string | undefined) => ["pages", "attachments", pageId] as const,
    /** Pages whose blocks link to this one (wiki-links). */
    backlinks: (pageId: string | undefined) => ["pages", "backlinks", pageId] as const,
    /** Organizations with which a page is shared (server). */
    sharedOrgs: (pageId: string | undefined) => ["pages", "sharedOrgs", pageId] as const,
    /** Local version history (bounded bucket) of a page. */
    versions: (pageId: string | undefined) => ["pages", "versions", pageId] as const,
    /** Trash: pages with soft-delete. */
    trash: (userId: string | undefined) => ["pages", "trash", userId] as const,
    /** Spreadsheet library (pages with workbook block). */
    workbooks: (userId: string | undefined) => ["pages", "workbooks", userId] as const,
    /** Presentation library (pages with deck block). */
    decks: (userId: string | undefined) => ["pages", "decks", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (profile + list-dialog SHARE cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    byPage: (pageId: string) => ["attachments", "page", pageId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    /**
     * Active user sessions (device list).
     *
     * PREFIX on purpose: `activeSessionsFor(id)` branches off it, so
     * invalidating `activeSessions` refreshes the list regardless of the current
     * session. Required by P23/CC-60 · P25: after revoking a device the list
     * must truly be refreshed; previously the key was inlined inside
     * the component and could not be invalidated from BootManager.
     */
    activeSessions: ["auth", "activeSessions"] as const,
    activeSessionsFor: (currentSessionId: string | undefined) =>
      ["auth", "activeSessions", currentSessionId] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
};
